import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./RadarPlaybackBar.css";

// Resolved once at load time — e.g. "EST", "MST", "GMT+5:30"
const LOCAL_TZ_ABBR = (() => {
  try {
    const parts = new Intl.DateTimeFormat("en", { timeZoneName: "short" }).formatToParts(new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch { return ""; }
})();

// Convert "HH:MM UTC" → local "HH:MM" using today's date.
function utcHHMMToLocal(utcStr) {
  if (!utcStr) return null;
  const m = utcStr.match(/^(\d{2}):(\d{2})/);
  if (!m) return null;
  const now = new Date();
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), +m[1], +m[2]));
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

const SITE_STATUS_TITLE = {
  online:       "Radar site online",
  initializing: "Radar initializing",
  stale:        "Radar feed delayed",
  offline:      "Radar site offline",
};

const SITE_STATUS_HINT = {
  initializing: "INIT",
  stale:        "DELAYED",
  offline:      "OFFLINE",
};

// Max number of frame blocks to render.
// Fewer blocks means wider, easier-to-tap blocks on all screen sizes.
const MAX_BLOCKS = 18;

/**
 * RadarPlaybackBar — compact integrated bottom control strip.
 *
 * Props:
 *   playing               {bool}
 *   frameIndex            {number}
 *   frameCount            {number}
 *   currentTime           {string|null}  "HH:MM UTC"
 *   firstTime             {string|null}  "HH:MM UTC"
 *   lastTime              {string|null}  "HH:MM UTC"
 *   liveMode              {bool}
 *   siteStatus            {string}       "online" | "stale" | "offline"
 *   onPlayPause           {fn}
 *   onPrev                {fn}
 *   onNext                {fn}
 *   onSeek                {fn(index)}
 *   onLatest              {fn}
 *   siteLabel             {string|null}  active radar site ICAO (e.g. "KCYS")
 *   productSlot           {ReactNode}    product chip only (desktop Row 1 / mobile Row 1 left)
 *   iconsSlot             {ReactNode}    tool icon buttons (desktop Row 1 / mobile Row 3 right)
 */
export default function RadarPlaybackBar({
  playing,
  frameIndex,
  frameCount,
  currentTime,
  firstTime,
  lastTime,
  liveMode,
  siteStatus,
  siteLabel,
  onPlayPause,
  onPrev,
  onNext,
  onSeek,
  onLatest,
  productSlot,
  iconsSlot,
}) {
  const safe = frameCount > 0 ? Math.max(0, Math.min(frameCount - 1, frameIndex)) : 0;

  // ── Hold-to-scrub state ──────────────────────────────────────────────────
  const [isScrubOpen, setIsScrubOpen]     = useState(false);
  // dragBlockIdx: which block is highlighted during active press-drag (null = not dragging)
  const [dragBlockIdx, setDragBlockIdx]   = useState(null);

  const holdRef        = useRef(null);
  const didHoldRef     = useRef(false); // true when hold fired, prevents tap-on-release
  const playBtnRef     = useRef(null);  // ref for pointer capture
  const scrubBlocksRef = useRef(null);  // ref to .rpb-blocks in overlay
  const pointerIdRef   = useRef(null);  // stored pointerId for capture/release

  // ── Frame block map: maps display-block index → actual frame index ──────
  const blockFrameMap = useMemo(() => {
    if (frameCount === 0) return [];
    if (frameCount <= MAX_BLOCKS) return Array.from({ length: frameCount }, (_, i) => i);
    return Array.from({ length: MAX_BLOCKS }, (_, i) =>
      Math.round((i / (MAX_BLOCKS - 1)) * (frameCount - 1))
    );
  }, [frameCount]);

  // The last block whose mapped frame is ≤ safe → that's the "active" block.
  const activeBlockIdx = useMemo(() => {
    let idx = 0;
    for (let i = 0; i < blockFrameMap.length; i++) {
      if (blockFrameMap[i] <= safe) idx = i;
    }
    return blockFrameMap.length > 0 ? idx : -1;
  }, [blockFrameMap, safe]);

  // During drag use dragBlockIdx for instant visual feedback; else follow actual frame.
  const displayActiveBlock = dragBlockIdx !== null ? dragBlockIdx : activeBlockIdx;

  // ── Play button: short tap = play/pause, long press = open scrub ─────────
  const handlePlayDown = useCallback((e) => {
    if (frameCount === 0) return;
    e.preventDefault();
    pointerIdRef.current = e.pointerId;
    didHoldRef.current = false;
    holdRef.current = setTimeout(() => {
      holdRef.current = null;
      didHoldRef.current = true;
      if (playing) onPlayPause(); // pause before scrubbing
      setIsScrubOpen(true);
      // Capture pointer so pointermove tracks even as finger slides off the button
      playBtnRef.current?.setPointerCapture(pointerIdRef.current);
    }, 380);
  }, [frameCount, playing, onPlayPause]);

  // Called for every pointer move while button holds capture — drives live scrub
  const handlePlayMove = useCallback((e) => {
    if (!isScrubOpen || !scrubBlocksRef.current || blockFrameMap.length === 0) return;
    e.preventDefault(); // prevent accidental scroll/pan on mobile
    const rect = scrubBlocksRef.current.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const bi = Math.round(frac * (blockFrameMap.length - 1));
    setDragBlockIdx(bi);
    onSeek(blockFrameMap[bi]);
  }, [isScrubOpen, blockFrameMap, onSeek]);

  const handlePlayUp = useCallback((e) => {
    if (holdRef.current) {
      clearTimeout(holdRef.current);
      holdRef.current = null;
    }
    if (didHoldRef.current) {
      // Was a hold/drag: finalize selection and close overlay
      playBtnRef.current?.releasePointerCapture(e.pointerId);
      setIsScrubOpen(false);
      setDragBlockIdx(null);
    } else {
      onPlayPause(); // short tap
    }
    didHoldRef.current = false;
  }, [onPlayPause]);

  // Cancel hold timer on pointer leave (before 380ms); does NOT close overlay once open
  // because pointer capture prevents pointerleave from firing while captured.
  const handlePlayLeave = useCallback(() => {
    if (holdRef.current) {
      clearTimeout(holdRef.current);
      holdRef.current = null;
    }
  }, []);

  // Clean up on interrupted gesture (phone call, browser interruption, etc.)
  const handlePlayCancel = useCallback(() => {
    if (holdRef.current) { clearTimeout(holdRef.current); holdRef.current = null; }
    setIsScrubOpen(false);
    setDragBlockIdx(null);
    didHoldRef.current = false;
  }, []);

  // Cleanup on unmount
  useEffect(() => () => { if (holdRef.current) clearTimeout(holdRef.current); }, []);

  // ── Scrub overlay keyboard nav ────────────────────────────────────────────

  function handleScrubKey(e) {
    switch (e.key) {
      case "ArrowLeft":  e.preventDefault(); onSeek(Math.max(0, safe - 1)); break;
      case "ArrowRight": e.preventDefault(); onSeek(Math.min(frameCount - 1, safe + 1)); break;
      case "Home":       e.preventDefault(); onSeek(0); break;
      case "End":        e.preventDefault(); onSeek(frameCount - 1); break;
      case "Escape":     e.preventDefault(); setIsScrubOpen(false); setDragBlockIdx(null); break;
      default: break;
    }
  }

  // ── Time helpers ─────────────────────────────────────────────────────────
  const hhmm = (t) => t ? t.replace(" UTC", "") : "—";
  const localHHMM = useMemo(() => utcHHMMToLocal(currentTime), [currentTime]);

  const isDragging = dragBlockIdx !== null;

  // ── LIVE/REVIEW badge shared render helper ────────────────────────────────
  const liveBadge = (extraClass = "") => (
    <span
      className={`rpb-mode rpb-mode--${liveMode ? "live" : "review"}${liveMode && siteStatus && siteStatus !== "online" ? ` rpb-site--${siteStatus}` : ""}${extraClass ? ` ${extraClass}` : ""}`}
      title={liveMode && siteStatus ? SITE_STATUS_TITLE[siteStatus] : undefined}
      aria-label={
        liveMode && siteStatus
          ? `LIVE. ${SITE_STATUS_TITLE[siteStatus]}`
          : liveMode ? "LIVE" : "REVIEW"
      }
    >
      <span className="rpb-mode-dot" aria-hidden="true" />
      {liveMode ? "LIVE" : "REVIEW"}
      {liveMode && SITE_STATUS_HINT[siteStatus] && (
        <span className="rpb-status-hint" aria-hidden="true">{SITE_STATUS_HINT[siteStatus]}</span>
      )}
    </span>
  );

  return (
    <div className="rpb" role="group" aria-label="Playback controls">

      {/* ── Hold-to-scrub overlay (anchored above bar) ──
          Kept as a direct child of .rpb (outside .rpb-inner) so the
          overflow-x:auto scroll container on .rpb-inner does not clip it. */}
      {isScrubOpen && (
        <div
          className={`rpb-scrub-overlay${isDragging ? " rpb-scrub-overlay--dragging" : ""}`}
          role="group"
          aria-label="Frame scrub"
          onKeyDown={handleScrubKey}
        >
          <span className="rpb-scrub-t" aria-hidden="true">{hhmm(firstTime)}</span>
          <div
            className="rpb-blocks"
            ref={scrubBlocksRef}
            role="group"
            aria-label="Frame timeline"
          >
            {blockFrameMap.map((fi, bi) => (
              <button
                key={bi}
                className={`rpb-block${bi === displayActiveBlock ? " rpb-block--active" : bi < displayActiveBlock ? " rpb-block--past" : ""}`}
                onClick={() => onSeek(fi)}
                aria-label={`Frame ${fi + 1} of ${frameCount}${fi === safe ? ", current" : ""}`}
                tabIndex={-1}
              >
                <span className="rpb-block-bar" aria-hidden="true" />
              </button>
            ))}
          </div>
          <span className="rpb-scrub-t rpb-scrub-t--end" aria-hidden="true">{hhmm(lastTime)}</span>
          <button
            className="rpb-scrub-close"
            onClick={() => { setIsScrubOpen(false); setDragBlockIdx(null); }}
            aria-label="Close scrub"
            title="Close (Esc)"
          >
            <svg viewBox="0 0 10 10" fill="none" aria-hidden="true">
              <line x1="1.5" y1="1.5" x2="8.5" y2="8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="8.5" y1="1.5" x2="1.5" y2="8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}

      {/* ── Inner scroll wrapper: contains both flex zones.
          overflow-x:auto here keeps .rpb itself at overflow:visible so
          the scrub overlay above the bar is never clipped. ── */}
      <div className="rpb-inner">

      {/* ── Left zone: transport controls + LIVE/REVIEW badge ── */}
      <div className="rpb-controls">

        <button
          className="rpb-btn rpb-btn--step"
          onClick={onPrev}
          disabled={frameCount <= 1}
          aria-label="Previous frame"
          title="Previous frame (←)"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M6 4v12h2V4H6zm2 6L16 4v12L8 10z" />
          </svg>
        </button>

        <button
          ref={playBtnRef}
          className={`rpb-btn rpb-btn--play${isScrubOpen ? " rpb-btn--play-scrub" : ""}`}
          onPointerDown={handlePlayDown}
          onPointerMove={handlePlayMove}
          onPointerUp={handlePlayUp}
          onPointerLeave={handlePlayLeave}
          onPointerCancel={handlePlayCancel}
          onContextMenu={(e) => e.preventDefault()}
          disabled={frameCount === 0}
          aria-label={playing ? "Pause" : "Play"}
          title={`${playing ? "Pause (Space)" : "Play (Space)"} · Hold to scrub`}
        >
          {playing && !isScrubOpen ? (
            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <rect x="4.5" y="3" width="4" height="14" rx="1.5" />
              <rect x="11.5" y="3" width="4" height="14" rx="1.5" />
            </svg>
          ) : (
            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M5 3.5l12 6.5-12 6.5V3.5z" />
            </svg>
          )}
        </button>

        <button
          className="rpb-btn rpb-btn--step"
          onClick={onNext}
          disabled={frameCount <= 1}
          aria-label="Next frame"
          title="Next frame (→)"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M14 4v12h-2V4h2zM12 10L4 4v12l8-6z" />
          </svg>
        </button>

        <div className="rpb-divider" aria-hidden="true" />

        {/* Site label + LIVE / REVIEW badge + Latest — desktop; hidden on mobile */}
        <div className="rpb-mode-group">
          {siteLabel && <span className="rpb-site-label">{siteLabel}</span>}
          {liveBadge()}
          {!liveMode && (
            <button
              className="rpb-latest-btn"
              onClick={onLatest}
              disabled={frameCount === 0}
              aria-label="Jump to latest frame"
              title="Jump to latest frame"
            >
              Latest
            </button>
          )}
        </div>

        {frameCount > 0 && (
          <>
            <div className="rpb-divider" aria-hidden="true" />
            <span
              className="rpb-frame-count"
              aria-label={`Frame ${safe + 1} of ${frameCount}`}
              aria-live="polite"
            >
              {safe + 1}<span className="rpb-frame-count-sep" aria-hidden="true">/</span>{frameCount}
            </span>
          </>
        )}

        {/* Icon tools — mobile Row 3 (right of play); hidden on desktop */}
        {iconsSlot && (
          <div className="rpb-icons-slot rpb-icons-slot--bottom">
            {iconsSlot}
          </div>
        )}

      </div>

      {/* ── Right zone: product chip + desktop icons + Frames trigger ── */}
      <div className="rpb-right">
        {(productSlot || iconsSlot) && (
          <div className="rpb-tools-wrap">
            {productSlot}
            {/* Icon tools — desktop Row 1 (beside product chip); hidden on mobile */}
            {iconsSlot && (
              <div className="rpb-icons-slot rpb-icons-slot--top">
                {iconsSlot}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Time display (direct child of .rpb-inner for mobile row reflow) ── */}
      <div className="rpb-now" aria-live="polite" aria-label="Current frame time">
        {/* Mobile-only: LIVE/REVIEW badge left of time */}
        {liveBadge("rpb-now-mode")}
        <span className="rpb-now-time">{hhmm(currentTime)}</span>
        <span className="rpb-now-tz">UTC</span>
        {localHHMM && LOCAL_TZ_ABBR && (
          <span className="rpb-now-local-group">
            <span className="rpb-now-sep" aria-hidden="true">·</span>
            <span className="rpb-now-local">{localHHMM}</span>
            <span className="rpb-now-tz">{LOCAL_TZ_ABBR}</span>
          </span>
        )}
      </div>

      </div>{/* end .rpb-inner */}

    </div>
  );
}
